// components/layouts/AdminLayout.jsx
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
    FiHome, FiLayers, FiGrid, FiPackage, FiUsers, FiLogOut, FiMenu, FiX, FiUser,
    FiTag, FiFileText, FiMessageSquare
} from 'react-icons/fi';

const AdminLayout = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const navigation = [
        { name: 'Dashboard', href: '/admin/dashboard', icon: FiHome },
        { name: 'Categories', href: '/admin/categories', icon: FiLayers },
        { name: 'Subcategories', href: '/admin/subcategories', icon: FiGrid },
        { name: 'Tags', href: '/admin/tags', icon: FiTag },
        { name: 'Products', href: '/admin/products', icon: FiPackage },
        { name: 'Content', href: '/admin/content', icon: FiFileText },
        { name: 'Reviews', href: '/admin/reviews', icon: FiMessageSquare },
        { name: 'Users', href: '/admin/users', icon: FiUsers },
    ];

    const isActive = (path) => {
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };

    return (
        <div className="h-screen flex overflow-hidden bg-background">
            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 md:hidden"
                    onClick={() => setSidebarOpen(false)}
                ></div>
            )}

            {/* Sidebar */}
            <div
                className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#f8f8f8] shadow-lg transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                    } md:translate-x-0 transition-transform duration-300 ease-in-out md:static md:z-0`}
            >
                <div className="flex flex-col h-full">
                    {/* Sidebar header */}
                    <div className="h-16 flex items-center justify-center border-b border-gray-200">
                        <Link to="/admin/dashboard" className="text-2xl font-bold text-primary-600">
                            radiance<span className="text-black">.</span>
                        </Link>
                        <button
                            className="absolute right-2 p-2 rounded-md text-gray-500 md:hidden"
                            onClick={() => setSidebarOpen(false)}
                        >
                            <FiX size={24} />
                        </button>
                    </div>

                    {/* Sidebar navigation */}
                    <nav className="flex-1 px-2 py-4 overflow-y-auto">
                        <div className="space-y-1">
                            {navigation.map((item) => (
                                <Link
                                    key={item.name}
                                    to={item.href}
                                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive(item.href)
                                        ? 'bg-primary-50 text-primary-600'
                                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                        }`}
                                >
                                    <item.icon
                                        className={`mr-3 h-5 w-5 ${isActive(item.href) ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'
                                            }`}
                                    />
                                    {item.name}
                                </Link>
                            ))}
                        </div>
                    </nav>

                    {/* User profile */}
                    {user && (
                        <div className="border-t border-gray-200 p-4">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                                        <FiUser className="text-primary-600" />
                                    </div>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm font-medium text-gray-700">{user.name}</p>
                                    <Link to="/admin/profile" className="text-xs text-gray-500 hover:text-primary-600">
                                        View profile
                                    </Link>
                                </div>
                                <button
                                    onClick={handleLogout}
                                    className="ml-auto p-1 rounded-full text-gray-400 hover:text-gray-500"
                                    title="Logout"
                                >
                                    <FiLogOut />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Top header */}
                <header className="bg-[#f8f8f8] shadow-sm">
                    <div className="h-16 flex items-center px-4">
                        <button
                            className="p-2 rounded-md text-gray-500 md:hidden"
                            onClick={() => setSidebarOpen(true)}
                        >
                            <FiMenu size={24} />
                        </button>
                        <div className="ml-4 md:ml-0">
                            <h1 className="text-lg font-medium text-gray-900">Admin Panel</h1>
                        </div>
                    </div>
                </header>

                {/* Main content */}
                <main className="flex-1 overflow-y-auto p-6 bg-background">
                    <div className="container mx-auto">{children}</div>
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;